import React, { useEffect, useMemo, useState } from 'react';
/**
 * MultiplicationGridRenderer — Fill hidden cells in a multiplication table.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { SparklesIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface EditableCell {
  key: string;
  row: number;
  col: number;
  rowValue: number;
  colValue: number;
}
export function MultiplicationGridRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const mode = config.mode || 'single_table';
  const [filledCells, setFilledCells] = useState<Record<string, string>>({});
  const editableCells = useMemo<EditableCell[]>(() => {
    if (mode === 'single_table') {
      const baseNumber = config.baseNumber || 1;
      const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      const hiddenCells: number[] = config.hiddenCells || [];
      return hiddenCells.filter(index => range[index] !== undefined).map(index => ({
        key: String(index),
        row: 0,
        col: index,
        rowValue: baseNumber,
        colValue: range[index]
      }));
    }
    const rows: number[] = config.rows || [];
    const cols: number[] = config.cols || [];
    const hiddenCells: Array<{
      row: number;
      col: number;
    }> = config.hiddenCells || [];
    return hiddenCells.filter(cell => rows[cell.row] !== undefined && cols[cell.col] !== undefined).map(cell => ({
      key: `${cell.row}-${cell.col}`,
      row: cell.row,
      col: cell.col,
      rowValue: rows[cell.row],
      colValue: cols[cell.col]
    }));
  }, [config, mode]);
  useEffect(() => {
    setFilledCells({});
  }, [question.id]);
  const totalHiddenCells = editableCells.length;
  useEffect(() => {
    const filledCount = Object.values(filledCells).filter(value => value.trim() !== '').length;
    onReadyChange?.(filledCount === totalHiddenCells && totalHiddenCells > 0);
  }, [filledCells, totalHiddenCells, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(filledCells), [filledCells]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleInputChange = (key: string, value: string) => {
    setFilledCells(previous => ({
      ...previous,
      [key]: value
    }));
  };
  const focusCell = (key: string) => {
    document.getElementById(`multiplication-grid-${question.id}-${key}`)?.focus();
  };
  const findDirectionalCell = (current: EditableCell, direction: 'left' | 'right' | 'up' | 'down') => {
    const candidates = editableCells.filter(cell => {
      if (direction === 'left') {
        return cell.row === current.row && cell.col < current.col;
      }
      if (direction === 'right') {
        return cell.row === current.row && cell.col > current.col;
      }
      if (direction === 'up') {
        return cell.col === current.col && cell.row < current.row;
      }
      return cell.col === current.col && cell.row > current.row;
    });
    return candidates.sort((a, b) => {
      const aDistance = direction === 'left' || direction === 'right' ? Math.abs(a.col - current.col) : Math.abs(a.row - current.row);
      const bDistance = direction === 'left' || direction === 'right' ? Math.abs(b.col - current.col) : Math.abs(b.row - current.row);
      return aDistance - bDistance;
    })[0];
  };
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, cell: EditableCell) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const index = editableCells.findIndex(candidate => candidate.key === cell.key);
      const nextCell = editableCells[index + 1];
      if (nextCell) {
        focusCell(nextCell.key);
      } else {
        event.currentTarget.blur();
      }
      return;
    }
    if (!event.altKey) return;
    const directionByKey: Record<string, 'left' | 'right' | 'up' | 'down'> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowUp: 'up',
      ArrowDown: 'down'
    };
    const direction = directionByKey[event.key];
    if (!direction) return;
    const nextCell = findDirectionalCell(cell, direction);
    if (nextCell) {
      event.preventDefault();
      focusCell(nextCell.key);
    }
  };
  const filledCount = Object.values(filledCells).filter(value => value.trim() !== '').length;
  const cellInputClass = (value: string) => `w-full h-full text-center font-bold rounded-md outline-none transition-all duration-200 ${value ? 'bg-indigo-50 border-2 border-indigo-300 text-indigo-800' : 'bg-amber-50 border-2 border-amber-300 text-gray-800'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white shadow-inner`;
  const headerCellClass = 'w-11 h-11 sm:w-12 sm:h-12 bg-indigo-600 text-white font-bold rounded-md text-center align-middle shadow-sm';
  const dataCellClass = 'w-11 h-11 sm:w-12 sm:h-12 bg-white border border-gray-200 text-gray-800 font-medium rounded-md text-center align-middle shadow-sm';
  const renderInput = (cell: EditableCell, headerIds: string[]) => <input id={`multiplication-grid-${question.id}-${cell.key}`} type="text" inputMode="numeric" pattern="[0-9]*" value={filledCells[cell.key] || ''} onChange={event => handleInputChange(cell.key, event.target.value)} onKeyDown={event => handleInputKeyDown(event, cell)} disabled={shouldValidate} aria-label={`Antwoord voor ${cell.rowValue} keer ${cell.colValue}`} aria-describedby={headerIds.join(' ')} className={cellInputClass(filledCells[cell.key] || '')} data-id="element-11803" data-mp="2v6a068e06n4y" />;
  const renderSingleTable = () => {
    const baseNumber = config.baseNumber || 1;
    const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const hiddenCells: number[] = config.hiddenCells || [];
    const tableId = `multiplication-grid-${question.id}`;
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-11804" data-mp="3skwm2yi3uyv9">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-11805" data-mp="3slogi5zyuqjy">
          <caption className="sr-only" data-id="element-11806" data-mp="1dac6o4sm05y">
            Tafel van {baseNumber}. Vul de lege uitkomsten in.
          </caption>
          <thead data-id="element-11807" data-mp="2zy22s4x0y5jd">
            <tr data-id="element-11808" data-mp="2zylnfte27813">
              <th id={`${tableId}-corner`} scope="col" className="w-11 h-11 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-11809" data-mp="34v28uftvj8ah">
                ×
              </th>
              {range.map((multiplier, index) => <th key={`header-${index}`} id={`${tableId}-column-${index}`} scope="col" className={headerCellClass} data-id="element-11810" data-mp="p34o93hafkp5" data-mpi={index}>
                  {multiplier}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-11811" data-mp="29njjw2eqzi2q">
            <tr data-id="element-11812" data-mp="29n0bpdewg81w">
              <th id={`${tableId}-row-0`} scope="row" className={headerCellClass} data-id="element-11813" data-mp="29okw4a7i2am1">
                {baseNumber}
              </th>
              {range.map((multiplier, index) => {
              if (hiddenCells.includes(index)) {
                const cell = editableCells.find(candidate => candidate.key === String(index));
                return <td key={`cell-${index}`} className="w-11 h-11 sm:w-12 sm:h-12" data-id="element-11814" data-mp="1lx6172i1yvkt">
                      {cell && renderInput(cell, [`${tableId}-row-0`, `${tableId}-column-${index}`])}
                    </td>;
              }
              return <td key={`cell-${index}`} className={dataCellClass} data-id="element-11815" data-mp="2p5bd056mxvav" data-mpi={index}>
                    {baseNumber * multiplier}
                  </td>;
            })}
            </tr>
          </tbody>
        </table>
      </div>;
  };
  const renderCrossTable = () => {
    const rows: number[] = config.rows || [];
    const cols: number[] = config.cols || [];
    const hiddenCells: Array<{
      row: number;
      col: number;
    }> = config.hiddenCells || [];
    const tableId = `multiplication-grid-${question.id}`;
    const isHidden = (row: number, col: number) => hiddenCells.some(cell => cell.row === row && cell.col === col);
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-11816" data-mp="1wh5rhg7rc29z">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-11817" data-mp="3lvbztyng21pj">
          <caption className="sr-only" data-id="element-11818" data-mp="3queucp5sspj9">
            Kruistabel. Vul de lege uitkomsten van de vermenigvuldigingen in.
          </caption>
          <thead data-id="element-11819" data-mp="1f836ilz3zogo">
            <tr data-id="element-11820" data-mp="1f7l2v4a4a7yq">
              <th id={`${tableId}-corner`} scope="col" className="w-11 h-11 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-11821" data-mp="1f4yvp5r51uh1">
                ×
              </th>
              {cols.map((columnValue, index) => <th key={`column-${index}`} id={`${tableId}-column-${index}`} scope="col" className={headerCellClass} data-id="element-11822" data-mp="1xxjchotkc94h" data-mpi={index}>
                  {columnValue}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-11823" data-mp="22wmfl3pzg59t">
            {rows.map((rowValue, rowIndex) => <tr key={`row-${rowIndex}`} data-id="element-11824" data-mp="dhy3l3lb0pcb" data-mpi={rowIndex}>
                <th id={`${tableId}-row-${rowIndex}`} scope="row" className={headerCellClass} data-id="element-11825" data-mp="dj0rd2nbybo0">
                  {rowValue}
                </th>
                {cols.map((columnValue, colIndex) => {
              if (isHidden(rowIndex, colIndex)) {
                const cell = editableCells.find(candidate => candidate.key === `${rowIndex}-${colIndex}`);
                return <td key={`cell-${rowIndex}-${colIndex}`} className="w-11 h-11 sm:w-12 sm:h-12" data-id="element-11826" data-mp="l514d7f6y5il">
                        {cell && renderInput(cell, [`${tableId}-row-${rowIndex}`, `${tableId}-column-${colIndex}`])}
                      </td>;
              }
              return <td key={`cell-${rowIndex}-${colIndex}`} className={dataCellClass} data-id="element-11827" data-mp="20lt3aa7dggfc" data-mpi={colIndex}>
                      {rowValue * columnValue}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>;
  };
  const progressPct = totalHiddenCells > 0 ? filledCount / totalHiddenCells * 100 : 0;
  return <div className="w-full flex flex-col space-y-4" data-id="element-11828" data-mp="3sfrfnovwkui5">
      <p className="text-center text-sm text-slate-500" data-id="element-11829" data-mp="3sdeocl4997sf">
        Vul de lege vakjes in. Gebruik Enter voor het volgende vakje of Alt met
        de pijltjestoetsen om door een rij of kolom te bewegen.
      </p>
      <div className="w-full bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100 shadow-inner" data-id="element-11830" data-mp="65ycq9rpwtu6">
        {mode === 'single_table' ? renderSingleTable() : renderCrossTable()}
      </div>
      <div className="flex flex-col items-center justify-center space-y-2" data-id="element-11831" data-mp="f2phdakc5h0d">
        <div className="flex items-center space-x-2 text-sm font-medium text-gray-600" data-id="element-11832" data-mp="k192j8r3uoro">
          <SparklesIcon className={`w-4 h-4 ${filledCount === totalHiddenCells ? 'text-amber-500' : 'text-gray-400'}`} aria-hidden="true" data-id="element-11833" data-mp="ozkcdjhegz9w" />
          <span aria-live="polite" data-id="element-11834" data-mp="2g3luv3lesp6o">
            {filledCount} van {totalHiddenCells} ingevuld
          </span>
        </div>
        <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-label="Voortgang ingevulde vakjes" aria-valuemin={0} aria-valuemax={totalHiddenCells} aria-valuenow={filledCount} data-id="element-11835" data-mp="2usic9r9cvejy">
          <div className="h-full bg-indigo-500 transition-all duration-300 ease-out" style={{
          width: `${progressPct}%`
        }} data-id="element-11836" data-mp="2zpryyz5d1c43" />
        </div>
      </div>
    </div>;
}